import type { OutputLanguage } from '../types';

/**
 * Intitulés de sections des DOCUMENTS produits — CV et lettres.
 *
 * Ces libellés ne passent PAS par `t()`, et c'est le point le plus important de
 * l'internationalisation : ils suivent la langue du DOCUMENT (`cvs.output_language`), jamais celle
 * de l'interface. Quelqu'un peut naviguer en anglais et exporter un CV français : le PDF doit alors
 * porter « Expériences professionnelles », pas « Work experience ».
 *
 * Utiliser `t()` ici serait un bug silencieux — le document sortirait dans la langue de l'app, et
 * rien ne le signalerait avant qu'un recruteur reçoive un CV moitié français moitié anglais.
 *
 * Les deux axes se croisent donc ici, et seulement ici. Le reste de l'application suit l'interface.
 */
const CV_SECTIONS: Record<OutputLanguage, Record<string, string>> = {
  fr: {
    experiences: 'Expériences professionnelles',
    projects: 'Projets personnels',
    education: 'Formation',
    skills: 'Compétences',
    languages: 'Langues',
    interests: 'Centres d’intérêt',
  },
  en: {
    experiences: 'Work experience',
    projects: 'Personal projects',
    education: 'Education',
    skills: 'Skills',
    languages: 'Languages',
    interests: 'Interests',
  },
};

export type CvSectionKey = keyof (typeof CV_SECTIONS)['fr'];

/** Intitulé d'une section de CV, dans la langue du DOCUMENT (et non de l'interface). */
export function cvSectionLabel(key: CvSectionKey, language: OutputLanguage = 'fr'): string {
  return CV_SECTIONS[language][key] ?? CV_SECTIONS.fr[key];
}

/**
 * Noms de langues PARLÉES proposés dans la rubrique correspondante du CV.
 *
 * Côté DOCUMENT, comme les intitulés de sections : ce que la personne choisit ici est recopié tel
 * quel dans le CV, puis part dans le prompt via serializeLanguages. Suggérer « Anglais » dans un CV
 * rédigé en anglais y écrirait du français.
 *
 * Les diplômes (TOEFL, IELTS, DELF…) sont des noms propres : ils ne se traduisent pas.
 */
const LANGUAGE_NAMES: Record<OutputLanguage, string[]> = {
  fr: ['Français', 'Anglais', 'Espagnol', 'Allemand', 'Italien'],
  en: ['French', 'English', 'Spanish', 'German', 'Italian'],
};

/** Le niveau « natif » n'est pas un code CEFR : c'est du texte, il suit donc le document. */
const NATIVE_LABEL: Record<OutputLanguage, string> = { fr: 'Natif', en: 'Native' };

export function quickLanguageNames(language: OutputLanguage = 'fr'): string[] {
  return LANGUAGE_NAMES[language] ?? LANGUAGE_NAMES.fr;
}

export function nativeLevelLabel(language: OutputLanguage = 'fr'): string {
  return NATIVE_LABEL[language] ?? NATIVE_LABEL.fr;
}

/**
 * « En cours » — valeur de FIN DE PÉRIODE, écrite dans le CV, pas un libellé d'interface.
 *
 * Cinquième croisement des deux axes, et le plus discret de la chaîne CV : la case à cocher qui
 * l'active s'intitule « Poste actuel » et relève de l'interface, mais la CHAÎNE PRODUITE part dans
 * la sérialisation envoyée au prompt (« ACME (Janv. 2024 – En cours) ») et se retrouve dans le CV
 * exporté. Un CV anglais afficherait « En cours » au milieu de ses dates.
 */
const ONGOING: Record<OutputLanguage, string> = { fr: 'En cours', en: 'Present' };

export function ongoingLabel(language: OutputLanguage = 'fr'): string {
  return ONGOING[language] ?? ONGOING.fr;
}
